import { collection, addDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { appConfig } from '../config';
import { isOnline } from '../utils/networkError';
import type { ErrorLog, UserReportCategory } from '../types';
import { uploadFile } from './storage';
import { recentActivity } from './usageLog';
import { classifySelfHelp, dismissSelfHelp, getSelfHelpOffer, offerSelfHelp, rememberSigninError, takePendingSigninError } from '../utils/selfHelp';
import { getUpdateInputs, isNewerVersion, setLiveVersion } from '../utils/updateStatus';

type ErrorLogType = ErrorLog['type'];

// Module-level user context — set once on login, cleared on logout
let currentUserId: string | undefined;
let currentUserRole: string | undefined;

export const setErrorLogUser = (userId?: string, userRole?: string): void => {
  currentUserId = userId;
  currentUserRole = userRole;
  if (userId) {
    // They just signed in, so a "sign out and back in" pop-up is already answered.
    if (getSelfHelpOffer() === 'signin') dismissSelfHelp();
    // A sign-in error from while they were signed out: record it now, in their name.
    const pending = takePendingSigninError();
    if (pending && db) {
      addDoc(collection(db, 'errorLogs'), { ...pending, userId, ...(userRole ? { userRole } : {}) }).catch(() => {});
    }
  }
};

/**
 * The live version isn't known yet (first seconds after opening, or offline so far): ask
 * once, and if this copy turns out to be old, offer the update. The entry itself is already
 * written — the server judges old copies on its own.
 */
const offerUpdateOnceKnown = (): void => {
  fetch('/version.json', { cache: 'no-store' })
    .then((res) => (res.ok ? res.json() : null))
    .then((body: { version?: unknown } | null) => {
      if (!body || typeof body.version !== 'string') return;
      setLiveVersion(body.version);
      if (isNewerVersion(body.version, appConfig.version)) offerSelfHelp('update');
    })
    .catch(() => {});
};

/**
 * Write an error to the Firestore errorLogs collection.
 * Fire-and-forget — never throws, never blocks the caller.
 * Skipped entirely in development to avoid noise.
 */
/**
 * The fields every entry carries, however it was raised. A caught error and a
 * user report land in the same collection and are read side by side, so the
 * diagnostic context has to be built in one place — or the next field added
 * goes missing on whichever path forgot it.
 */
const baseEntry = (type: ErrorLogType, message: string, context: string): Omit<ErrorLog, 'id'> => ({
  timestamp: new Date().toISOString(),
  type,
  message,
  context,
  url: window.location.pathname,
  // Which build, and whether the phone had a connection — without these, an
  // error that arrives just after a release cannot be pinned to either side
  // of it, and a dropped signal is indistinguishable from a real failure.
  version: appConfig.version,
  online: isOnline(),
  ...(currentUserId ? { userId: currentUserId } : {}),
  ...(currentUserRole ? { userRole: currentUserRole } : {}),
});

export const logError = (
  error: unknown,
  context: string,
  type: ErrorLogType = 'manual'
): void => {
  if (import.meta.env.DEV) return;
  if (!db) return;

  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? (error.stack ?? undefined) : undefined;

  // What they tapped and opened just before — the single most useful clue for finding why.
  const breadcrumbs = recentActivity();

  const signedIn = !!auth?.currentUser;
  const { liveVersion } = getUpdateInputs();
  const selfHelp = classifySelfHelp(error, type, { running: appConfig.version, live: liveVersion, signedIn });

  const entry: Omit<ErrorLog, 'id'> = {
    ...baseEntry(type, message, context),
    ...(stack ? { stack } : {}),
    ...(breadcrumbs.length ? { breadcrumbs } : {}),
    ...(selfHelp ? { selfHelp } : {}),
  };

  // The update pop-up says "something went wrong just now", so only for failures the person
  // saw: a crash or an uncaught error. A quiet caught error is still tagged for the server.
  const visible = type === 'react_boundary' || type === 'unhandled_error' || type === 'unhandled_rejection';
  // Firebase can drop an expired session while the app still shows the working screen
  // (the app's own user isn't cleared), so the app's signed-in user counts too.
  const onWorkingScreen = signedIn || !!currentUserId;
  if (selfHelp === 'signin' && onWorkingScreen) offerSelfHelp('signin');
  else if (selfHelp === 'update' && visible) offerSelfHelp('update');
  else if (!selfHelp && !liveVersion && visible) offerUpdateOnceKnown();

  // Signed out of Firebase, the write would be refused; it's written after they sign back in.
  // On the sign-in screen itself there's no pop-up (onWorkingScreen is false).
  if (selfHelp === 'signin' && !signedIn) {
    rememberSigninError(entry);
    return;
  }

  addDoc(collection(db, 'errorLogs'), entry).catch(() => {
    // Intentionally silent — error logging must never cascade into another error
  });
};

export const submitUserReport = async (
  message: string,
  category: UserReportCategory,
  viewName: string,
  screenshotFile?: File | null
): Promise<void> => {
  if (!db) throw new Error('Database not available');

  let screenshotUrl: string | undefined;

  if (screenshotFile) {
    const userId = currentUserId ?? 'anonymous';
    const timestamp = Date.now();
    const ext = screenshotFile.type.includes('png') ? 'png' : 'jpg';
    const path = `bugReports/${userId}/${timestamp}.${ext}`;
    screenshotUrl = await uploadFile(path, screenshotFile);
  }

  const entry: Omit<ErrorLog, 'id'> = {
    ...baseEntry('user_report', message, viewName),
    category,
    pageInfo: {
      viewName,
      url: window.location.pathname,
      userAgent: navigator.userAgent,
    },
    ...(screenshotUrl ? { screenshotUrl } : {}),
  };

  await addDoc(collection(db, 'errorLogs'), entry);
};
